<script setup>
import { ref } from 'vue'
import SettingsLayout from '@/Layouts/SettingsLayout.vue'
import TeachingStyleSelection from '@/Components/Forms/TeachingStyleSelection.vue'

const props = defineProps({
  styles: Array,
})

const selectedStyle = ref(null)
const styleRef = ref(null)
const loading = ref(false)
const errors = ref({})
const successMessage = ref('')

const saveStyle = async () => {
  if (styleRef.value && !styleRef.value.validate()) {
    return
  }

  loading.value = true
  errors.value = {}
  successMessage.value = ''

  try {
    const response = await fetch('/teacher/settings/teaching-style', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/json',
        'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
      },
      body: JSON.stringify({
        selected_style: selectedStyle.value,
      })
    })

    const data = await response.json()

    if (!response.ok) {
      if (data.errors) {
        errors.value = data.errors
      } else {
        errors.value = { general: data.message || 'Failed to save. Please try again.' }
      }
      loading.value = false
      return
    }

    successMessage.value = 'Teaching style updated successfully!'
    loading.value = false

    setTimeout(() => {
      successMessage.value = ''
    }, 3000)

  } catch (err) {
    errors.value = { general: 'An error occurred. Please try again.' }
    loading.value = false
  }
}
</script>

<template>
  <SettingsLayout>
    <div class="space-y-6">
      <div v-if="successMessage" class="bg-green-50 border-2 border-green-200 rounded-2xl p-5 flex items-center">
        <div class="flex-shrink-0 w-10 h-10 bg-green-500 rounded-full flex items-center justify-center mr-4">
          <span class="material-symbols-outlined text-white text-xl">check_circle</span>
        </div>
        <div>
          <p class="text-green-900 font-semibold">Success</p>
          <p class="text-green-700 text-sm">{{ successMessage }}</p>
        </div>
      </div>

      <div v-if="errors.general" class="bg-red-50 border-2 border-red-200 rounded-2xl p-5 flex items-center">
        <div class="flex-shrink-0 w-10 h-10 bg-red-500 rounded-full flex items-center justify-center mr-4">
          <span class="material-symbols-outlined text-white text-xl">error</span>
        </div>
        <div>
          <p class="text-red-900 font-semibold">Error</p>
          <p class="text-red-700 text-sm">{{ errors.general }}</p>
        </div>
      </div>

      <TeachingStyleSelection
        ref="styleRef"
        :styles="styles"
        :model-value="selectedStyle"
        @update:model-value="(val) => selectedStyle = val"
        :errors="errors"
      />

      <div class="flex justify-end pt-6 border-t border-gray-200">
        <button
          @click="saveStyle"
          :disabled="loading"
          class="px-8 py-3.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl font-bold hover:from-blue-700 hover:to-blue-800 transition-all shadow-xl shadow-blue-500/40 hover:shadow-2xl hover:shadow-blue-600/50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center text-base"
        >
          <span v-if="loading" class="material-symbols-outlined animate-spin mr-2 text-xl">progress_activity</span>
          {{ loading ? 'Saving...' : 'Save Changes' }}
          <span v-if="!loading" class="material-symbols-outlined ml-2 text-xl">check</span>
        </button>
      </div>
    </div>
  </SettingsLayout>
</template>
